import { useEffect, useMemo, useRef, useState } from 'react';
import { LoadMoreButton } from '@rio-cloud/rio-uikit';
import { useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';

import {
    type AegOccurrence,
    type AegOccurrencesFilters,
    type AegOccurrenceSort,
    buildAegOccurrenceUpdate,
    EMPTY_AEG_STATUS_COUNT,
    exportAegOccurrencesXlsx,
    fetchAegOccurrence,
    fetchAegOccurrences,
    fetchAegOccurrencesStatusCount,
    updateAegOccurrence,
} from '../../api/aegOccurrences';
import BigNumber from './components/BigNumber';
import Board from './components/Board';
import { defaultVisibleColumns, type ColumnKey } from './components/ColumnSelector';
import Filter, { emptyFilters } from './components/Filter';
import Header from './components/Header';
import RequestDetail from './components/RequestDetail';
import ResultHeader, { type ViewMode } from './components/ResultHeader';
import Table from './components/Table';
import {
    AEG_NEXT_STAGES,
    AEG_STAGE_BY_STATUS,
    AEG_STATUS_BY_STAGE,
    AegApprovalTypeEnum,
    type AegAssignToEnum,
    type AegServiceTypeEnum,
    AegVehicleTypeEnum,
    formatAegStage,
    type Request,
    type StageKey,
} from './types';
import './styles.css';
import NewAEG from './components/NewAEG';

const PAGE_SIZE = 10;

const SEARCH_DEBOUNCE_MS = 400;

const SORT_BY_VIEW_SORT: Record<string, AegOccurrenceSort> = {
    recent: 'MOST_RECENT',
    old: 'OLDEST',
    'value-high': 'HIGHEST_VALUE',
    'value-low': 'LOWEST_VALUE',
};

const useDebouncedValue = <T,>(value: T, delay: number) => {
    const [debounced, setDebounced] = useState(value);

    useEffect(() => {
        const timeout = window.setTimeout(() => setDebounced(value), delay);
        return () => window.clearTimeout(timeout);
    }, [value, delay]);

    return debounced;
};

const assignedToLabel = (assignedTo: AegOccurrence['assignedTo']) =>
    typeof assignedTo === 'string' ? assignedTo : (assignedTo?.name ?? '');

const mapOccurrenceToRequest = (occurrence: AegOccurrence, fallbackId: string): Request => {
    const status = occurrence.status ?? occurrence.moveTo ?? '';

    return {
        id: String(occurrence.id ?? occurrence.aegNumber ?? occurrence.requestNumber ?? fallbackId),
        aegNumber:
            occurrence.aegNumber != null && String(occurrence.aegNumber).trim() !== ''
                ? String(occurrence.aegNumber)
                : '',
        client: occurrence.customer ?? occurrence.client ?? '',
        model: occurrence.model ?? '',
        plate: occurrence.licensePlate ?? occurrence.plate ?? '',
        chassis: occurrence.chassis ?? '',
        approval: occurrence.approvalType ?? AegApprovalTypeEnum.REGIONAL,
        value: Number(occurrence.repairTotal ?? 0),
        owner: occurrence.createdBy ?? occurrence.responsible ?? '',
        assignedTo: assignedToLabel(occurrence.assignedTo) || occurrence.assignTo || '',
        stage: status ? AEG_STAGE_BY_STATUS[status] : 'abertura',
        dealer: occurrence.dn ?? occurrence.dealer ?? '',
        date: (occurrence.createdAt ?? occurrence.date ?? '').slice(0, 10),
        regional: occurrence.regional ?? '',
        // cell: occurrence.cell ?? '',
        vehicleType: occurrence.vehicleType ?? AegVehicleTypeEnum.TRUCK,
        serviceType: occurrence.serviceType ?? '',
        sg: occurrence.warrantyRequest ?? occurrence.serviceOrder ?? '',
        repairDate: (occurrence.repairDate ?? '').slice(0, 10),
        stageTime: occurrence.stageTime ?? '',
        saleDate: (occurrence.saleDate ?? '').slice(0, 10),
        km: Number(occurrence.odometer ?? 0),
        warrantyType: occurrence.warrantyType ?? '',
    };
};

export default function AegManagement() {
    const intl = useIntl();
    const [rows, setRows] = useState<Request[]>([]);
    const [filters, setFilters] = useState(emptyFilters);
    const [stage, setStage] = useState<StageKey | null>(null);
    const [view, setView] = useState<ViewMode>('table');
    const [sort, setSort] = useState('recent');
    // A página só vale para o conjunto de filtros em que foi pedida; mudou o filtro, volta para a primeira.
    const [pageState, setPageState] = useState({ key: '', page: 0 });
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [statusCount, setStatusCount] = useState(EMPTY_AEG_STATUS_COUNT);
    const [visibleColumns, setVisibleColumns] = useState<ColumnKey[]>(defaultVisibleColumns);
    const [searchParams, setSearchParams] = useSearchParams();
    const [toast, setToast] = useState('');
    const movingIds = useRef(new Set<string>());
    const [newAEG, setNewAEG] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    const selectedId = searchParams.get('id');

    const typedFilters = useMemo(
        () => ({ searchTerm: filters.query, dn: filters.dealer, regional: filters.regional, createdBy: filters.owner }),
        [filters.query, filters.dealer, filters.regional, filters.owner]
    );
    const textFilters = useDebouncedValue(typedFilters, SEARCH_DEBOUNCE_MS);

    const statusCountFilters = useMemo<Omit<AegOccurrencesFilters, 'status'>>(
        () => ({
            ...textFilters,
            approvalType: filters.approval as AegApprovalTypeEnum | '',
            vehicleType: filters.vehicleType as AegVehicleTypeEnum | '',
            serviceType: filters.serviceType as AegServiceTypeEnum | '',
            assignTo: filters.assignedTo as AegAssignToEnum | '',
        }),
        [textFilters, filters.approval, filters.vehicleType, filters.serviceType, filters.assignedTo]
    );
    const statusCountKey = JSON.stringify(statusCountFilters);
    const apiSort = SORT_BY_VIEW_SORT[sort];
    const listKey = `${statusCountKey}|${stage ?? ''}|${apiSort}`;
    const page = pageState.key === listKey ? pageState.page : 0;

    const showSkeleton = isLoading && page === 0;

    const onLoadMore = () => {
        if (!isLoading && rows.length < total) {
            setPageState({ key: listKey, page: page + 1 });
        }
    };

    useEffect(() => {
        let cancelled = false;

        const loadOccurrences = async () => {
            setIsLoading(true);
            try {
                const response = await fetchAegOccurrences({
                    ...statusCountFilters,
                    status: stage ? [AEG_STATUS_BY_STAGE[stage]] : undefined,
                    sort: apiSort,
                    page,
                    size: PAGE_SIZE,
                });
                if (cancelled) {
                    return;
                }

                const requests = response.content.map((occurrence, index) =>
                    mapOccurrenceToRequest(occurrence, `AEG-${page}-${index}`)
                );
                setRows(current => (page === 0 ? requests : [...current, ...requests]));
                setTotal(response.totalElements);
            } catch {
                if (!cancelled) {
                    setToast(intl.formatMessage({ id: 'intl-msg:support.aeg.list.loadError' }));
                }
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        };

        loadOccurrences();

        return () => {
            cancelled = true;
        };
    }, [listKey, page, reloadKey]);

    useEffect(() => {
        let cancelled = false;

        const loadStatusCount = async () => {
            try {
                const counts = await fetchAegOccurrencesStatusCount(statusCountFilters);
                if (!cancelled) {
                    setStatusCount(counts);
                }
            } catch {
                if (!cancelled) {
                    setStatusCount(EMPTY_AEG_STATUS_COUNT);
                }
            }
        };

        loadStatusCount();

        return () => {
            cancelled = true;
        };
    }, [statusCountKey, reloadKey]);

    useEffect(() => {
        if (!toast) {
            return;
        }
        const timeout = window.setTimeout(() => setToast(''), 2400);
        return () => window.clearTimeout(timeout);
    }, [toast]);

    useEffect(() => {
        const focusSearch = (event: KeyboardEvent) => {
            if (event.key === '/' && !['INPUT', 'TEXTAREA', 'SELECT'].includes((event.target as HTMLElement).tagName)) {
                event.preventDefault();
                document.getElementById('aeg-search')?.focus();
            }
        };
        document.addEventListener('keydown', focusSearch);
        return () => document.removeEventListener('keydown', focusSearch);
    }, []);

    const statusCountTotal = Object.values(statusCount).reduce((sum, value) => sum + value, 0);
    const openRequest = (id: string) => {
        setSearchParams(prev => {
            const params = new URLSearchParams(prev);
            params.set('id', id);
            return params;
        });
    };

    const closeRequest = () => {
        setSearchParams(
            prev => {
                const params = new URLSearchParams(prev);
                params.delete('id');
                return params;
            },
            { replace: true }
        );
    };

    const move = (id: string, nextStage: StageKey) => {
        setRows(current => current.map(row => (row.id === id ? { ...row, stage: nextStage } : row)));
        setToast(intl.formatMessage({ id: 'intl-msg:support.aeg.board.moved' }, { id, stage: formatAegStage(intl, nextStage) }));
    };

    const moveCard = async (id: string, nextStage: StageKey) => {
        const current = rows.find(row => row.id === id);
        if (!current || current.stage === nextStage || movingIds.current.has(id)) {
            return;
        }
        if (!AEG_NEXT_STAGES[current.stage].includes(nextStage)) {
            setToast(
                intl.formatMessage(
                    { id: 'intl-msg:support.aeg.board.move.invalid' },
                    { stage: formatAegStage(intl, nextStage) }
                )
            );
            return;
        }

        movingIds.current.add(id);
        try {
            const occurrence = await fetchAegOccurrence(id);
            await updateAegOccurrence(
                id,
                buildAegOccurrenceUpdate(occurrence, { status: AEG_STATUS_BY_STAGE[nextStage] })
            );
            setRows(rowsState => rowsState.map(row => (row.id === id ? { ...row, stage: nextStage } : row)));
            setToast(intl.formatMessage({ id: 'intl-msg:support.aeg.board.moved' }, { id, stage: formatAegStage(intl, nextStage) }));
            try {
                setStatusCount(await fetchAegOccurrencesStatusCount(statusCountFilters));
            } catch {
                // A etapa já foi salva; os totais são atualizados na próxima consulta.
            }
        } catch {
            setToast(intl.formatMessage({ id: 'intl-msg:support.aeg.board.move.error' }));
        } finally {
            movingIds.current.delete(id);
        }
    };

    const assign = (id: string, assignTo: string) => {
        setRows(current => current.map(row => (row.id === id ? { ...row, assignedTo: assignTo } : row)));
    };

    const exportRows = async () => {
        try {
            const blob = await exportAegOccurrencesXlsx({
                ...statusCountFilters,
                status: stage ? [AEG_STATUS_BY_STAGE[stage]] : undefined,
                sort: apiSort,
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `solicitacoes-aeg-${new Date().toISOString().slice(0, 10)}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            setToast(`${total} solicitações exportadas`);
        } catch {
            setToast(intl.formatMessage({ id: 'intl-msg:support.aeg.list.exportError' }));
        }
    };

    return (
        <main className='aeg-page-root padding-15 bg-lighter text-color-darkest text-size-14'>
            <Header />

            <Filter filters={filters} onFiltersChange={setFilters} onStageChange={setStage} stage={stage} />

            <BigNumber
                counts={statusCount}
                onSelect={key => setStage(current => (current === key ? null : key))}
                selected={stage}
                total={statusCountTotal}
            />

            <section className='aeg-page-results panel panel-default margin-bottom-0 rounded bg-white'>
                <ResultHeader
                    count={rows.length}
                    onExport={exportRows}
                    onSortChange={setSort}
                    onVisibleColumnsChange={setVisibleColumns}
                    onViewChange={setView}
                    sort={sort}
                    visibleColumns={visibleColumns}
                    view={view}
                    openNewAEGModal={() => setNewAEG(true)}
                />

                {view === 'table' ? (
                    <Table
                        isLoading={showSkeleton}
                        onSelect={openRequest}
                        rows={rows}
                        visibleColumns={visibleColumns}
                    />
                ) : (
                    <Board onMove={moveCard} onSelect={openRequest} rows={rows} selectedStage={stage} />
                )}

                {!showSkeleton && total > 0 ? (
                    <LoadMoreButton
                        loaded={rows.length}
                        total={total}
                        onLoadMore={onLoadMore}
                        loadMoreMessage={intl.formatMessage({ id: 'intl-msg:monitoring.loadMore' })}
                        noMoreMessage={intl.formatMessage({ id: 'intl-msg:monitoring.allLoaded' })}
                    />
                ) : null}
            </section>

            {selectedId ? (
                <RequestDetail
                    id={selectedId}
                    onAssign={assignTo => assign(selectedId, assignTo)}
                    onClose={closeRequest}
                    onMove={nextStage => move(selectedId, nextStage)}
                    onSaved={() => {
                        setPageState({ key: listKey, page: 0 });
                        setReloadKey(current => current + 1);
                    }}
                />
            ) : null}

            {newAEG ? (
                <NewAEG
                    onClose={() => setNewAEG(false)}
                    onCreated={() => {
                        setPageState({ key: listKey, page: 0 });
                        setReloadKey(current => current + 1);
                    }}
                />
            ) : null}

            {toast ? (
                <output className='aeg-page-toast padding-10 rounded shadow-default text-medium bg-darkest text-color-white'>
                    {toast}
                </output>
            ) : null}
        </main>
    );
}
